import { useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { cn } from "@/lib/cn";

export type TutorialStep = {
  title: string;
  body: string;
  cta?: string;
  href?: string;
};

/**
 * TutorialCoach — dismissable floating panel for the Beginner scenario.
 *
 * Renders a small bottom-right card that walks the user through the first
 * three actions of the game: visit Marketplace, pick an affordable
 * property, apply for mortgage. Hidden once `localStorage.rentier.tutorial`
 * is marked complete for a given player id.
 */
export function TutorialCoach({
  playerId,
  month,
  propertiesOwned,
  onNavigate,
}: {
  playerId: number;
  month: number;
  propertiesOwned: number;
  onNavigate: (path: string) => void;
}) {
  const storageKey = `rentier.tutorial.${playerId}`;
  const [dismissed, setDismissed] = useState<boolean>(
    () => typeof window !== "undefined" && localStorage.getItem(storageKey) === "done",
  );

  if (dismissed) return null;

  // Pick which step to show based on player progress. Once they own 1+
  // property, the tutorial auto-completes.
  const step = pickStep({ month, propertiesOwned });
  if (step === null) {
    // Auto-complete
    localStorage.setItem(storageKey, "done");
    return null;
  }

  const dismiss = () => {
    localStorage.setItem(storageKey, "done");
    setDismissed(true);
  };

  return (
    <div
      className={cn(
        "fixed bottom-4 left-4 right-4 z-40 max-w-xs",
        "sm:left-auto sm:right-6 sm:bottom-6",
        "rounded-2xl border border-amber/40 bg-surface-raised p-4",
        "shadow-[0_20px_50px_-20px_rgba(245,178,99,0.4)]",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-amber-bright">
            Krok {step.num} / 3 · Průvodce
          </div>
          <h3 className="mt-1 font-serif text-base font-semibold">
            {step.title}
          </h3>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Zavřít průvodce"
          className="rounded-md p-1 text-ink-dim hover:bg-bg-raised hover:text-ink"
        >
          <X size={14} />
        </button>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-ink-muted">{step.body}</p>
      {step.cta && step.href && (
        <button
          type="button"
          onClick={() => onNavigate(step.href!)}
          className={cn(
            "mt-3 flex w-full items-center justify-center gap-1.5",
            "rounded-lg bg-amber px-3 py-2 text-[12px] font-semibold text-[#10141d]",
            "hover:brightness-110",
          )}
        >
          {step.cta} <ArrowRight size={12} />
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        className="mt-2 w-full text-center text-[10px] text-ink-dim hover:text-ink"
      >
        Už to zvládnu sám/a — skrýt
      </button>
    </div>
  );
}

function pickStep({
  month,
  propertiesOwned,
}: {
  month: number;
  propertiesOwned: number;
}): (TutorialStep & { num: number }) | null {
  if (propertiesOwned >= 1) return null;

  if (month === 0) {
    return {
      num: 1,
      title: "Vítej v Rentier 2050!",
      body:
        "Tvůj cíl: vybudovat pasivní příjem z nemovitostí. Pro začátek mrkni na trh a najdi první byt. Doporučujeme zaškrtnout filtr „Pro tvůj rozpočet“.",
      cta: "Jít na trh nemovitostí",
      href: "/marketplace",
    };
  }

  if (month < 3) {
    return {
      num: 2,
      title: "Hledej chytře",
      body:
        "Yield (roční výnos) 4 %+ je slušný. RISK SCORE pod 30 = bezpečná sázka. Klikni na kartu pro detail a zvaž koupi na hypotéku.",
      cta: "Otevřít trh",
      href: "/marketplace",
    };
  }

  return {
    num: 3,
    title: "Posuň čas",
    body:
      "Hra postupuje po měsících — klikni „Další měsíc“ dole vlevo. Postupně ušetříš na akontaci. Portfolio ti rost, když vyberou nájem.",
  };
}
